import React, { useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapPin, NotebookPen, ReceiptText, CheckCircle } from "lucide-react";
import api from "../lib/api";
import Services from "../lib/services";
import { useNotifications } from "../contexts/NotificationContext";

const ACCENT = "linear-gradient(135deg, #6f12ff 0%, #9c27ff 100%)";
const DELIVERY_FEE = 5;
const OTHER_FEES = 3;

const cardStyle = {
  background: "#ffffff",
  borderRadius: 18,
  border: "1px solid #f0ecf7",
  padding: 16,
  boxShadow: "0 6px 18px rgba(77, 30, 126, 0.07)",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #ece7f4",
  borderRadius: 12,
  padding: "11px 13px",
  outline: "none",
  fontSize: 12,
  color: "#221b29",
  background: "#fcfbfe",
  marginTop: 9,
};

const formatMoney = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const SummaryLine = ({ label, amount, strong = false }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
    <span style={{ fontSize: 11, color: strong ? "#18141f" : "#847c95", fontWeight: strong ? 700 : 500 }}>{label}</span>
    <span style={{ fontSize: 11, color: "#18141f", fontWeight: strong ? 800 : 600 }}>{formatMoney(amount)}</span>
  </div>
);

const OrderPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems = [], totalPrice = 0, pharmacyId = null } = location.state || {};
  const { addNotification } = useNotifications();

  const [deliveryInfo, setDeliveryInfo] = useState({
    fullName: "",
    phone: "",
    address: "",
    city: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showSuccessSheet, setShowSuccessSheet] = useState(false);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  const subtotal = totalPrice || 0;
  const taxes = 0;
  const orderTotal = subtotal + OTHER_FEES + DELIVERY_FEE + taxes;

  const handleChange = (e) => {
    setDeliveryInfo({ ...deliveryInfo, [e.target.name]: e.target.value });
  };

  const handlePlaceOrder = async () => {
    if (!deliveryInfo.fullName || !deliveryInfo.address || !deliveryInfo.phone) {
      setMessage("Please fill in all delivery details.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const paymentData = {
        userId: localStorage.getItem("userId"),
        providerId: "6996f83cad941d32a067a6f4",
        amount: orderTotal,
        type: "drugs",
        serviceDetails: "Purchase of Drugs",
      };

      const payment = await Services.wallet.payProvider(paymentData);
      if (!payment?.success) {
        setMessage(payment?.message || "Payment failed. Try again.");
        return;
      }

      await api.post(
        "/api/orders",
        {
          items: cartItems.map((item) => ({
            medicine: item._id,
            quantity: item.quantity,
            price: item.price,
            name: item.name,
          })),
          totalPrice: orderTotal,
          pharmacy: pharmacyId,
          paymentMethod,
          deliveryInfo: {
            ...deliveryInfo,
            notes: instructions,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
          }
        },
      );

      setMessage("Order placed successfully.");
      localStorage.removeItem("cart");
      addNotification({
        type: 'order_placed',
        title: 'Order Placed',
        body: `Your pharmacy order of ${formatMoney(orderTotal)} was placed successfully.`,
        icon: '🛍️',
        data: { pharmacyId },
      });
      setShowSuccessSheet(true);
    } catch (error) {
      console.error(error);
      setMessage("Failed to place order. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f3f0f8", padding: "20px 24px 40px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "grid", gridTemplateColumns: "34px 1fr 34px", alignItems: "center", marginBottom: 28 }}>
          <i
            className="fi fi-br-angle-left"
            onClick={() => navigate(-1)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: "pointer", fontSize: "20px", background: "rgba(255,255,255,0.2)", padding: "6px 10px", borderRadius: "50%", transition: "background 0.2s ease", color: "#333" }}
          ></i>
          <h1 style={{ margin: 0, textAlign: "center", fontSize: 18, fontWeight: 700, color: "#18141f" }}>Order Confirmation</h1>
          <div />
        </div>

        {/* Sections */}
        <div style={{ display: "grid", gap: 16 }}>
          <section style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: "#221b29" }}>
              <MapPin size={15} color="#7b1cff" /> Delivery Address
            </div>
            <input name="fullName" value={deliveryInfo.fullName} onChange={handleChange} placeholder="Recipient full name" style={inputStyle} />
            <input name="phone" value={deliveryInfo.phone} onChange={handleChange} placeholder="Phone number" style={inputStyle} />
            <input name="address" value={deliveryInfo.address} onChange={handleChange} placeholder="House/Street address" style={inputStyle} />
            <input name="city" value={deliveryInfo.city} onChange={handleChange} placeholder="City" style={inputStyle} />
          </section>

          <section style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: "#221b29" }}>
              <NotebookPen size={15} color="#7b1cff" /> Additional Instruction
            </div>
            <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Type message" rows={3} style={{ ...inputStyle, resize: "vertical", minHeight: 92 }} />
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} style={inputStyle}>
              <option value="Qureo-Wallet">Qureo Health Wallet</option>
              <option value="Card">Dependent Wallet</option>

            </select>
          </section>

          <section style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: "#221b29", marginBottom: 12 }}>
              <ReceiptText size={15} color="#7b1cff" /> Order Summary
            </div>
            {cartItems.length > 0 ? (
              <>
                <div style={{ display: "grid", gap: 12 }}>
                  {cartItems.map((item) => (
                    <div key={item._id} style={{ display: "grid", gridTemplateColumns: "52px 1fr auto", gap: 10, alignItems: "center" }}>
                      <div style={{ width: 52, height: 52, borderRadius: 12, overflow: "hidden", background: "#fbfafe", border: "1px solid #f0ecf7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {item.image ? <img src={item.image} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ fontSize: 12, color: "#8f87a0", fontWeight: 700 }}>RX</div>}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#18141f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
                        <div style={{ fontSize: 10, color: "#9188a2", marginTop: 3 }}>{item.meta || "Prescription order"}</div>
                        <div style={{ fontSize: 11, color: "#18141f", fontWeight: 700, marginTop: 4 }}>{formatMoney(item.price)}</div>
                      </div>
                      <div style={{ fontSize: 10, color: "#9188a2", fontWeight: 700 }}>{item.quantity}X</div>
                    </div>
                  ))}
                </div>
                <div style={{ height: 1, background: "#ece7f4", margin: "14px 0" }} />
                <SummaryLine label="Subtotal" amount={subtotal} />
                <SummaryLine label="Taxes" amount={taxes} />
                <SummaryLine label="Other Fees" amount={OTHER_FEES} />
                <SummaryLine label="Delivery Fees" amount={DELIVERY_FEE} />
                <div style={{ height: 1, background: "#ece7f4", margin: "12px 0" }} />
                <SummaryLine label="Total cost" amount={orderTotal} strong />
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: "#847c95" }}>Your cart is empty.</p>
            )}
          </section>
        </div>

        {/* Button */}
        <button type="button" onClick={handlePlaceOrder} disabled={loading || cartItems.length === 0} style={{ width: "100%", marginTop: 24, border: "none", borderRadius: 14, padding: "16px 18px", background: ACCENT, color: "#fff", fontWeight: 700, fontSize: 14, boxShadow: "0 18px 24px rgba(124, 58, 237, 0.28)", cursor: loading || cartItems.length === 0 ? "not-allowed" : "pointer", opacity: loading || cartItems.length === 0 ? 0.6 : 1 }}>
          {loading ? "Processing..." : "Confirm & Pay"}
        </button>

        {/* Message */}
        {message && (
          <p style={{ margin: "16px 0 0", textAlign: "center", fontSize: 13, color: message.includes("success") ? "#168b5b" : "#d14343" }}>
            {message}
          </p>
        )}

        {showSuccessSheet && (
          <>
            <div
              onClick={() => setShowSuccessSheet(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15, 12, 24, 0.45)",
                zIndex: 1200,
              }}
            />
            <div
              style={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                background: "#ffffff",
                borderTopLeftRadius: 28,
                borderTopRightRadius: 28,
                padding: "14px 16px 24px",
                zIndex: 1201,
                textAlign: "center",
              }}
            >
              <div style={{ width: 46, height: 5, borderRadius: 999, background: "#ece8f5", margin: "0 auto 18px" }} />
              <div style={{ width: 40, height: 40, borderRadius: 999, margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center", background: "#7f18f4" }}>
                <CheckCircle size={18} color="#fff" />
              </div>
              <h3 style={{ margin: "0 0 8px", fontSize: 34, fontWeight: 700, color: "#15121f" }}>Congratulations!!!</h3>
              <p style={{ margin: "0 0 18px", color: "#6f6a7d", fontSize: 16, lineHeight: 1.45 }}>
                Congratulations, your medicine order has been placed successfully.
              </p>
              <button
                type="button"
                onClick={() => navigate("/orders")}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 14,
                  padding: "14px 16px",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 700,
                  background: ACCENT,
                }}
              >
                Track my Order
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderPage;